import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import PharmacyFolaSmartCard from "@/pages/pharmacy/PharmacyFolaSmartCard.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import StockThumb from "@/components/pharmacy/StockThumb.jsx";
import {
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  PHARMACY_NOTIFICATIONS_STORAGE_KEY,
  PHARMACY_PROFILE_CHANGED_EVENT,
  PHARMACY_PROFILE_STORAGE_KEY,
  recomputeStockAlerts,
  readPharmacyNotificationsForActor,
  readPharmacyProfileForActor,
} from "@/services/pharmacyLaneService.js";
import {
  getPharmacyInventoryItem,
  listPharmacyInventoryItems,
  savePharmacyInventoryItem,
} from "@/services/pharmacyInventoryService.js";
import { resolvePaymentCurrency } from "@/services/marketPaymentService.js";

const MUTED_TEXT = "text-[rgb(var(--clr-on-surface-variant))]";
const PRIMARY_TEXT = "text-[rgb(var(--clr-on-surface))]";
const ICON_EXPAND_MORE = "/icons/appointments/dashboard/expand_more.svg";
const CURRENCY_OPTIONS = [
  "AOA",
  "BWP",
  "FC",
  "SZL",
  "LSL",
  "MWK",
  "MZN",
  "NAD",
  "ZAR",
  "TZS",
  "ZMW",
  "ZWG",
  "CHF",
  "EUR",
  "GBP",
  "USD",
];

const INVENTORY_DRAFT_STORAGE_PREFIX = "afrimed:pharmacy:inventory:draft:v1:";
const INVENTORY_FORM_KEYS = [
  "id",
  "item",
  "detail",
  "category",
  "stockId",
  "currentStock",
  "minimalStock",
  "unitPrice",
  "supplier",
  "branch",
  "currency",
  "batchNumber",
  "expiryDate",
  "binLocation",
  "imageUrl",
];

function getInventoryDraftKey({ userId = "", itemId = "" } = {}) {
  return `${INVENTORY_DRAFT_STORAGE_PREFIX}${String(userId || "anonymous").trim() || "anonymous"}:${String(itemId || "new").trim() || "new"}`;
}

function hasMeaningfulInventoryDraft(draft = {}) {
  if (!draft || typeof draft !== "object") return false;
  return [
    draft.item,
    draft.stockId,
    draft.detail,
    draft.category,
    draft.currentStock,
    draft.minimalStock,
    draft.unitPrice,
    draft.supplier,
    draft.branch,
  ].some((value) => String(value ?? "").trim());
}

function getDraftFieldKeys(draft = {}) {
  if (!draft || typeof draft !== "object") return [];
  return INVENTORY_FORM_KEYS.filter((key) => String(draft?.[key] ?? "").trim());
}

function mergeHydratedInventoryItem({ base = {}, row = {}, draft = {}, touched = new Set() } = {}) {
  const next = {
    ...base,
    ...row,
  };

  for (const key of INVENTORY_FORM_KEYS) {
    if (touched.has(key)) {
      next[key] = draft?.[key] ?? "";
    }
  }

  return next;
}

function createEmptyItem(profile = {}) {
  const defaultCurrency = resolvePaymentCurrency({
    explicitCurrency: profile?.currency,
    country: profile?.country,
    fallbackCurrency: "ZAR",
  });
  return {
    item: "",
    detail: "",
    category: "",
    stockId: "",
    currentStock: "",
    minimalStock: "",
    unitPrice: "",
    supplier: "",
    branch: String(profile?.pharmacyName || profile?.tradingName || "").trim(),
    currency: defaultCurrency,
    batchNumber: "",
    expiryDate: "",
    binLocation: "",
    imageUrl: "",
  };
}

function InventoryCurrencyButton({ currencyCode = "ZAR", open = false, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="
        inline-flex h-10 shrink-0 items-center gap-1 rounded-[12px]
        bg-[rgba(var(--clr-on-surface),0.04)] px-3
        text-label-large text-[rgb(var(--clr-on-surface-variant))]
        hover:bg-[rgba(var(--clr-on-surface),0.06)]
      "
      aria-haspopup="menu"
      aria-expanded={open}
    >
      <span>{currencyCode}</span>
      <img
        src={ICON_EXPAND_MORE}
        alt=""
        className={["h-4 w-4 opacity-80 transition-transform", open ? "rotate-180" : ""].join(" ")}
        draggable="false"
      />
    </button>
  );
}

function InventoryUnitPriceField({
  label,
  value,
  onChange,
  currencyCode = "ZAR",
  menuOpen = false,
  onToggleMenu,
  onCurrencyChange,
}) {
  return (
    <div className="relative w-full rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 pt-2 pb-2.5">
      <label className={`block text-label-medium ${MUTED_TEXT}`}>{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <InventoryCurrencyButton
          currencyCode={currencyCode}
          open={menuOpen}
          onToggle={onToggleMenu}
        />
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange(event?.target?.value || "")}
          placeholder="0.00"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-body-large text-[rgb(var(--clr-on-surface))] outline-none placeholder:text-[rgb(var(--clr-on-surface-variant))]"
        />
      </div>

      {menuOpen ? (
        <div
          className="
            absolute left-4 top-[calc(100%+8px)] z-20 min-w-[180px] overflow-hidden rounded-[12px]
            border border-[rgba(var(--clr-outline-variant),0.55)]
            bg-[rgb(var(--clr-surface-cont-highest))]
            shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]
          "
          role="menu"
          aria-label="Inventory currency"
        >
          {CURRENCY_OPTIONS.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={currencyCode === code}
              onClick={() => {
                onCurrencyChange?.(code);
                onToggleMenu?.();
              }}
              className="flex h-11 w-full items-center justify-between px-4 text-left text-body-medium text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.04)]"
            >
              <span>{code}</span>
              {currencyCode === code ? (
                <span className="text-label-small text-[rgb(var(--afrimed-primary))]">Selected</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function PharmacyInventoryItemCreatePage() {
  const navigate = useNavigate();
  const { itemId = "" } = useParams();
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const actor = { userId: auth?.user?.id, email: auth?.user?.email };
  const readCurrentProfile = () => readPharmacyProfileForActor(actor);
  const [profile, setProfile] = React.useState(readCurrentProfile);
  const [notifications, setNotifications] = React.useState(() => readPharmacyNotificationsForActor(actor));
  const [values, setValues] = React.useState(() => createEmptyItem(readCurrentProfile()));
  const [loading, setLoading] = React.useState(Boolean(itemId));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [unitPriceMenuOpen, setUnitPriceMenuOpen] = React.useState(false);

  const userId = String(auth?.user?.id || "").trim();
  const userEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const isEditing = Boolean(String(itemId || "").trim());
  const draftKey = React.useMemo(
    () => getInventoryDraftKey({ userId, itemId: isEditing ? itemId : "new" }),
    [isEditing, itemId, userId],
  );
  const dirtyRef = React.useRef(false);
  const touchedFieldsRef = React.useRef(new Set());
  const currencyCode = React.useMemo(
    () =>
      resolvePaymentCurrency({
        explicitCurrency: values?.currency || profile?.currency,
        country: profile?.country,
        fallbackCurrency: "ZAR",
      }),
    [profile, values?.currency],
  );

  React.useEffect(() => {
    const refreshProfile = () => {
      const next = readCurrentProfile();
      setProfile(next);
      setValues((current) => ({
        ...current,
        branch: current.branch || String(next?.pharmacyName || next?.tradingName || "").trim(),
        currency:
          current.currency ||
          resolvePaymentCurrency({
            explicitCurrency: next?.currency,
            country: next?.country,
            fallbackCurrency: "ZAR",
          }),
      }));
    };
    const refreshNotifications = () => setNotifications(readPharmacyNotificationsForActor(actor));
    refreshProfile();
    refreshNotifications();
    const handleStorage = (event) => {
      if (!event?.key || event.key === PHARMACY_PROFILE_STORAGE_KEY) refreshProfile();
      if (!event?.key || event.key === PHARMACY_NOTIFICATIONS_STORAGE_KEY) refreshNotifications();
    };

    window.addEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, [actor.userId, actor.email]);

  React.useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = window.sessionStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!hasMeaningfulInventoryDraft(draft)) return;
      const draftKeys = getDraftFieldKeys(draft);
      draftKeys.forEach((key) => touchedFieldsRef.current.add(key));
      setValues((current) => ({
        ...current,
        ...draft,
      }));
      dirtyRef.current = draftKeys.length > 0;
    } catch {
      // Draft recovery is best effort only.
    }
  }, [draftKey]);

  React.useEffect(() => {
    if (!draftKey || !dirtyRef.current || saving) return;
    try {
      window.sessionStorage.setItem(draftKey, JSON.stringify(values));
    } catch {
      // Do not block pharmacy work on browser storage quota or privacy mode.
    }
  }, [draftKey, saving, values]);

  React.useEffect(() => {
    let alive = true;
    if (!isEditing) {
      setLoading(false);
      return undefined;
    }
    if (!userId && !userEmail) {
      setLoading(false);
      return undefined;
    }

    async function hydrateItem() {
      setLoading(true);
      setError("");
      try {
        const row = await getPharmacyInventoryItem({
          userId,
          email: userEmail,
          itemId,
        });
        if (!alive) return;
        if (!row) {
          setError(t("pharmacy.inventory.edit.missing", "We couldn’t find this stock item anymore."));
        } else {
          setValues((current) =>
            mergeHydratedInventoryItem({
              base: createEmptyItem(readCurrentProfile()),
              row,
              draft: current,
              touched: touchedFieldsRef.current,
            }),
          );
        }
      } catch (err) {
        if (!alive) return;
        setError(
          String(
            err?.message ||
              t("pharmacy.inventory.edit.loadError", "We couldn’t load this stock item yet."),
          ),
        );
      } finally {
        if (alive) setLoading(false);
      }
    }

    hydrateItem();
    return () => {
      alive = false;
    };
  }, [isEditing, itemId, t, userEmail, userId]);

  const canSave =
    String(values.item || "").trim() &&
    String(values.stockId || "").trim() &&
    String(values.currentStock || "").trim() &&
    String(values.minimalStock || "").trim() &&
    String(values.unitPrice || "").trim();

  function handleChange(key, nextValue) {
    dirtyRef.current = true;
    touchedFieldsRef.current.add(key);
    setValues((current) => ({ ...current, [key]: nextValue }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSave || saving) return;

    setSaving(true);
    setError("");
    try {
      await savePharmacyInventoryItem({
        userId,
        email: userEmail,
        item: {
          ...values,
          id: isEditing ? values.id || itemId : "",
        },
        profile,
      });
      try {
        window.sessionStorage.removeItem(draftKey);
      } catch {
        // ignore draft cleanup failures
      }
      const nextRows = await listPharmacyInventoryItems({
        userId,
        email: userEmail,
        profile,
      });
      await recomputeStockAlerts({
        profile,
        inventoryRows: Array.isArray(nextRows) ? nextRows : [],
        fallbackQueue: [],
        currentUserId: userId,
        currentUserEmail: userEmail,
      });
      navigate("/pharmacy/inventory", { replace: true });
    } catch (err) {
      setError(
        String(
          err?.message ||
            t("pharmacy.inventory.create.saveError", "We couldn’t save this stock item yet."),
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <PharmacyDesktopHeader section="inventory" notifications={notifications} />

      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-10">
        <TopBar
          title={
            isEditing
              ? t("pharmacy.inventory.edit.title", "Edit stock item")
              : t("pharmacy.inventory.create.title", "Add stock item")
          }
          subtitle={
            isEditing
              ? t(
                  "pharmacy.inventory.edit.subtitle",
                  "Update this branch inventory line so stock control and low-stock alerts stay current.",
                )
              : t(
                  "pharmacy.inventory.create.subtitle",
                  "Create a real inventory line so stock control, prescription routing, and pharmacy totals can start hydrating from branch truth.",
                )
          }
          onBack={() => navigate("/pharmacy/inventory")}
          titleWrapperClassName="mt-2 mb-5"
        />

        <div className="max-w-[1160px] space-y-6">
          <PharmacyFolaSmartCard
            title="Add a medicine line"
            body="Capture the item name, strength, category, stock level, reorder level, unit price, supplier, branch, batch number, expiry date, bin location, and optional image URL."
            cta="Help me add stock"
            route="/pharmacy/messages/fola"
            promptKey="add_stock_item"
            returnTo={isEditing ? `/pharmacy/inventory/${itemId}/edit` : "/pharmacy/inventory/new"}
          />
          <div className="max-w-[760px]">
            <p className={`text-body-medium ${MUTED_TEXT}`}>
              {t(
                "pharmacy.inventory.create.copy",
                "This writes directly into the branch inventory profile used by the Pharmacy dashboard. It replaces the old template fallback with real stock data.",
              )}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error ? (
              <div className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
                {error}
              </div>
            ) : null}
            <section className="space-y-4">
              <h2 className={`text-title-large ${PRIMARY_TEXT}`}>
                {t("pharmacy.inventory.create.details", "Item details")}
              </h2>
              <div className="grid gap-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.common.item", "Item")}
                    value={values.item}
                    onChange={(next) => handleChange("item", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.stockId", "Stock ID")}
                    value={values.stockId}
                    onChange={(next) => handleChange("stockId", next)}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.detail", "Strength / detail")}
                    value={values.detail}
                    onChange={(next) => handleChange("detail", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.common.category", "Category")}
                    value={values.category}
                    onChange={(next) => handleChange("category", next)}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.currentStock", "Current stock")}
                    value={values.currentStock}
                    onChange={(next) => handleChange("currentStock", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.minimalStock", "Minimal stock")}
                    value={values.minimalStock}
                    onChange={(next) => handleChange("minimalStock", next)}
                  />
                  <InventoryUnitPriceField
                    label={t("pharmacy.inventory.unitPrice", "Unit price")}
                    value={values.unitPrice}
                    onChange={(next) => handleChange("unitPrice", next)}
                    currencyCode={currencyCode}
                    menuOpen={unitPriceMenuOpen}
                    onToggleMenu={() => setUnitPriceMenuOpen((current) => !current)}
                    onCurrencyChange={(next) => handleChange("currency", next)}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.supplier", "Supplier")}
                    value={values.supplier}
                    onChange={(next) => handleChange("supplier", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.branch", "Branch")}
                    value={values.branch}
                    onChange={(next) => handleChange("branch", next)}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.batchNumber", "Batch number")}
                    value={values.batchNumber}
                    onChange={(next) => handleChange("batchNumber", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.expiryDate", "Expiry date")}
                    value={values.expiryDate}
                    onChange={(next) => handleChange("expiryDate", next)}
                  />
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.binLocation", "Bin location")}
                    value={values.binLocation}
                    onChange={(next) => handleChange("binLocation", next)}
                  />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <TextField56
                    tone="surface"
                    label={t("pharmacy.inventory.imageUrl", "Image URL")}
                    value={values.imageUrl}
                    onChange={(next) => handleChange("imageUrl", next)}
                  />
                  <div className="rounded-[12px] bg-[rgba(var(--afrimed-primary),0.04)] px-4 py-3">
                    <div className={`text-label-medium ${PRIMARY_TEXT}`}>
                      {t("pharmacy.inventory.imagePreview", "Image preview")}
                    </div>
                    <div className="mt-3 flex items-center gap-[10px]">
                      <StockThumb
                        src={String(values.imageUrl || "").trim()}
                        alt={values.item || "Medicine image preview"}
                        size="row"
                      />
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-body-medium ${PRIMARY_TEXT}`}>
                          {String(values.item || "").trim() ||
                            t("pharmacy.inventory.imagePreviewName", "Medicine image preview")}
                        </div>
                        <p className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                          {String(values.detail || "").trim() ||
                            t(
                              "pharmacy.inventory.imageHelp",
                              "Paste a hosted image URL for this medicine. If left empty, AfriMed will keep the stock placeholder.",
                            )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="flex flex-wrap items-center justify-start gap-2">
              <Button type="submit" disabled={!canSave || saving || loading}>
                {saving
                  ? t("common.saving", "Saving…")
                  : isEditing
                    ? t("pharmacy.inventory.edit.save", "Save changes")
                    : t("pharmacy.inventory.create.save", "Save stock item")}
              </Button>
              <Button type="button" variant="text" onClick={() => navigate("/pharmacy/inventory")}>
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
